/**
 * The Desk — the home screen, and it is Needs you.
 *
 * Until Oct 2026 this was 900 lines of CRM widgets (deal-flow velocity
 * against a "market benchmark" that did not exist, top clients, a live
 * activity feed, an insights generator, a commission calculator) with the
 * real work — every decision waiting on a person — one click away on its own
 * page. Now the decisions are the page. Beside them: the money due in the
 * next seven days, from the same calendar the Money page and the portal
 * read, and the reminder that the list can be worked from Claude. Below
 * them: where the book stands (TheDesk: AUM, guaranteed, pipeline, fees
 * booked, the cash waterfall — real figures only, with an em dash where the
 * record holds nothing), and the setup checklist until it is done.
 *
 * Kept from before: the first-run three-card welcome for an agency with no
 * records yet, the team-member welcome, the sample-data banner, the install
 * prompt, the onboarding tour and wizard.
 */

import { useEffect, useMemo, useState, useCallback } from 'react'
import { useQuery, useQueryClient, useIsRestoring } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import type { Deal, Client, Prospect, Contract } from '../types'
import { listDeals } from '../api/deals'
import { listClients } from '../api/clients'
import { listProspects } from '../api/prospects'
import { listContracts } from '../api/contracts'
import { getAgencyCommissionSettings } from '../api/commissions'
import { pb } from '../lib/pocketbase'
import { useInstallPrompt } from '../hooks/useInstallPrompt'
import { useSession } from '../hooks/useSession'
import { useMobile } from '../hooks/useMobile'
import { usePermissions } from '../hooks/usePermissions'
import { setPref } from '../utils/userPreferences'
import { useTeam } from '../hooks/useTeam'
import { WorkViewToggleInline } from '../components/WorkViewToggle'
import TheDesk from '../components/desk/TheDesk'
import { MoneyThisWeek, SayItToClaude } from '../components/desk/DeskRail'
import NeedsYouList from '../components/NeedsYouList'
import { hasDemoData, clearDemoData } from '../utils/demoData'
import { showToast } from '../components/Toast'
import { queryKeys } from '../utils/queryKeys'
import { agencyCommissionBlocker } from '../utils/commissionCoverage'
import { OnboardingChecklist } from '../components/OnboardingChecklist'
import CaptureInbox from '../components/CaptureInbox'
import ContactSuggestions from '../components/ContactSuggestions'
import { DashboardSkeleton } from '../components/Skeleton'
import OnboardingTour from '../components/OnboardingTour'
import OnboardingWizard, { useOnboardingWizard } from '../components/OnboardingWizard'
import {
  PlusIcon, ContractIcon, DealIcon, RocketIcon, UserPlusIcon, CheckIcon, TargetIcon,
} from './dashboard/DashboardIcons'
import styles from './Dashboard.module.css'

const ONBOARDING_COMPLETE_KEY = 'dealpoint_onboarding_complete'

export default function Dashboard() {
  const [showOnboarding, setShowOnboarding] = useState(false)
  const [showTeamWelcome, setShowTeamWelcome] = useState(false)
  const [demoBannerDismissed, setDemoBannerDismissed] = useState(false)
  const [hasDemoDataState, setHasDemoDataState] = useState(false)
  const [clearingDemo, setClearingDemo] = useState(false)
  const { canInstall, promptInstall } = useInstallPrompt()
  const { user } = useSession()
  const { isAssistant } = usePermissions()
  const queryClient = useQueryClient()
  const { teamMembers, agencyId } = useTeam()
  const isMobile = useMobile()

  const currentUser = pb.authStore.model as any
  const hasAgency = !!currentUser?.agency_id
  const { show: showOnboardingWizard, dismiss: dismissOnboardingWizard } = useOnboardingWizard()

  // Check if demo data exists (async — works across browsers and devices)
  useEffect(() => {
    if (!agencyId) return
    let cancelled = false
    hasDemoData(agencyId).then(has => {
      if (!cancelled) setHasDemoDataState(has)
    })
    return () => { cancelled = true }
  }, [agencyId])

  // ── What the checklist and the first-run screen need: does anything exist yet ──
  const dealsQuery = useQuery({
    queryKey: queryKeys.deals.list({ limit: 100 }),
    queryFn: () => listDeals({ limit: 100 }),
    enabled: hasAgency,
  })
  const clientsQuery = useQuery({
    queryKey: queryKeys.clients.list({ limit: 100 }),
    queryFn: () => listClients({ limit: 100 }),
    enabled: hasAgency,
  })
  const contractsQuery = useQuery({
    queryKey: queryKeys.contracts.list(),
    queryFn: () => listContracts(),
    enabled: hasAgency,
  })
  // For the checklist: without a commission split on file nothing books.
  const settingsQuery = useQuery({
    queryKey: ['agency_commission_settings', 'mine'] as const,
    queryFn: () => getAgencyCommissionSettings(),
    enabled: hasAgency,
  })
  const prospectsQuery = useQuery({
    queryKey: queryKeys.prospects.list({}),
    queryFn: () => listProspects({}),
    enabled: hasAgency,
  })

  const deals = useMemo(() => dealsQuery.data ?? ([] as Deal[]), [dealsQuery.data])
  const clients = useMemo(() => clientsQuery.data ?? ([] as Client[]), [clientsQuery.data])
  const contracts = useMemo(() => contractsQuery.data ?? ([] as Contract[]), [contractsQuery.data])
  const prospects = useMemo(() => prospectsQuery.data ?? ([] as Prospect[]), [prospectsQuery.data])

  const isRestoring = useIsRestoring()
  const criticalLoading = dealsQuery.isLoading || clientsQuery.isLoading || contractsQuery.isLoading
  const criticalError = dealsQuery.error || clientsQuery.error || contractsQuery.error
  const loading = criticalLoading || isRestoring

  // ── Onboarding state ──
  const onboardingProgress = useMemo(() => ({
    addedClient: clients.length > 0,
    createdDeal: deals.length > 0,
    addedProspect: prospects.length > 0,
  }), [clients.length, deals.length, prospects.length])

  useEffect(() => {
    if (criticalLoading) return
    const onboardingComplete = localStorage.getItem(ONBOARDING_COMPLETE_KEY)
    const hasNoData = clients.length === 0 && deals.length === 0 && prospects.length === 0
    if (!onboardingComplete && hasNoData) {
      setShowOnboarding(true)
    }
  }, [criticalLoading, clients.length, deals.length, prospects.length])

  useEffect(() => {
    if (!user?.id) return
    const role = (user as any).role as string | undefined
    if (role === 'admin' || role === 'superadmin') return
    const key = `team_welcome_seen_${user.id}`
    if (!localStorage.getItem(key)) {
      setShowTeamWelcome(true)
    }
  }, [user?.id])

  const currentDate = new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })

  const getUserFirstName = () => {
    if (!user) return 'there'
    const name = user.name || user.email || ''
    const firstName = name.split(' ')[0].split('@')[0]
    return firstName ? firstName.charAt(0).toUpperCase() + firstName.slice(1) : 'there'
  }

  const skipOnboarding = useCallback(() => {
    localStorage.setItem(ONBOARDING_COMPLETE_KEY, 'true')
    setPref('onboarding_complete', true)
    setShowOnboarding(false)
  }, [])

  const handleClearDemo = useCallback(async () => {
    if (!agencyId || clearingDemo) return
    setClearingDemo(true)
    try {
      await clearDemoData(agencyId)
      setHasDemoDataState(false)
      await queryClient.invalidateQueries()
    } catch (err) {
      showToast((err as any).message || 'Failed to clear demo data', 'error')
    } finally {
      setClearingDemo(false)
    }
  }, [agencyId, clearingDemo, queryClient])

  const onboardingCompleteCount = Object.values(onboardingProgress).filter(Boolean).length
  const onboardingTotalSteps = 3
  const onboardingPercent = Math.round((onboardingCompleteCount / onboardingTotalSteps) * 100)

  if (loading) {
    return <DashboardSkeleton />
  }

  if (criticalError) {
    const errorMessage = criticalError instanceof Error ? criticalError.message : String(criticalError)
    return (
      <div className={styles.dashboard}>
        <div style={{ padding: '2rem', textAlign: 'center' }}>
          <h2>Failed to load the Desk</h2>
          <p style={{ color: 'var(--color-text-secondary)', marginBottom: '1rem' }}>{errorMessage}</p>
          <button onClick={() => window.location.reload()} style={{ padding: '0.5rem 1rem', cursor: 'pointer' }}>
            Retry
          </button>
        </div>
      </div>
    )
  }

  // Show onboarding for first-time users
  if (showOnboarding) {
    return (
      <div className={styles.onboardingContainer}>
        <div className={styles.onboardingHeader}>
          <div className={styles.onboardingIcon}>
            <RocketIcon />
          </div>
          <h1 className={styles.onboardingTitle}>Welcome to Reppr, {getUserFirstName()}!</h1>
          <p className={styles.onboardingSubtitle}>
            Let's get your agency set up in 3 simple steps to start managing your clients.
          </p>
        </div>

        <div className={styles.onboardingCards}>
          <div className={`${styles.onboardingCard} ${onboardingProgress.addedClient ? styles.completed : ''}`}>
            <div className={styles.onboardingCardIcon} style={{ background: 'rgba(200, 164, 92, 0.15)', color: '#C8A45C' }}>
              <UserPlusIcon />
            </div>
            <h3 className={styles.onboardingCardTitle}>Add your first client</h3>
            <p className={styles.onboardingCardDesc}>
              Build your roster by adding clients, their details, and key information.
            </p>
            <Link
              to="/clients/new"
              className={`${styles.onboardingCardBtn} ${onboardingProgress.addedClient ? styles.completed : styles.primary}`}
            >
              {onboardingProgress.addedClient ? (<><CheckIcon /> Added</>) : (<><PlusIcon /> Add Client</>)}
            </Link>
          </div>

          <div className={`${styles.onboardingCard} ${onboardingProgress.createdDeal ? styles.completed : ''}`}>
            <div className={styles.onboardingCardIcon} style={{ background: 'rgba(168, 135, 63, 0.15)', color: '#A8873F' }}>
              <DealIcon />
            </div>
            <h3 className={styles.onboardingCardTitle}>Create a deal</h3>
            <p className={styles.onboardingCardDesc}>
              Draft a contract or agreement and track it through your pipeline.
            </p>
            <Link
              to="/deals/new"
              className={`${styles.onboardingCardBtn} ${onboardingProgress.createdDeal ? styles.completed : ''}`}
            >
              {onboardingProgress.createdDeal ? (<><CheckIcon /> Created</>) : (<><ContractIcon /> New Deal</>)}
            </Link>
          </div>

          <div className={`${styles.onboardingCard} ${onboardingProgress.addedProspect ? styles.completed : ''}`}>
            <div className={styles.onboardingCardIcon} style={{ background: 'rgba(78, 154, 106, 0.15)', color: '#4E9A6A' }}>
              <TargetIcon />
            </div>
            <h3 className={styles.onboardingCardTitle}>Add a prospect</h3>
            <p className={styles.onboardingCardDesc}>
              Track potential clients you're working to sign to your agency.
            </p>
            <Link
              to="/prospects/new"
              className={`${styles.onboardingCardBtn} ${onboardingProgress.addedProspect ? styles.completed : ''}`}
            >
              {onboardingProgress.addedProspect ? (<><CheckIcon /> Added</>) : (<><PlusIcon /> Add Prospect</>)}
            </Link>
          </div>
        </div>

        <div className={styles.onboardingProgress}>
          <div className={styles.onboardingProgressInfo}>
            <span>{onboardingCompleteCount} of {onboardingTotalSteps} complete</span>
            <span className={styles.onboardingPercent}>{onboardingPercent}%</span>
          </div>
          <div className={styles.onboardingProgressBar}>
            <div className={styles.onboardingProgressFill} style={{ width: `${onboardingPercent}%` }} />
          </div>
        </div>

        <button onClick={skipOnboarding} className={styles.skipOnboardingBtn}>
          Skip setup for now
        </button>

        <div className={styles.onboardingFooter}>
          <p>&copy; {new Date().getFullYear()} Reppr Sports Management Platform. All rights reserved.</p>
        </div>
      </div>
    )
  }

  // One thing can stop every commission figure on the platform being real,
  // and it is fixed in one place. The checklist mentions it, but that hides
  // itself once the rest is done — so it is also said here, where the money
  // is, for as long as it is true. The claim comes from the nightly ledger
  // sync's own conclusion, not from a second guess at the same question.
  const blocker = agencyCommissionBlocker(contracts)
  const blockerNote = blocker && (
    <div className={styles.blocker}>
      {blocker.message}{' '}
      <Link to="/commissions" style={{ color: 'var(--color-primary)', fontWeight: 'var(--font-weight-medium)' }}>
        Set the split
      </Link>
    </div>
  )

  if (isMobile) {
    return (
      <div className={styles.mobileDashboard}>
        <header className={styles.mobileHeader}>
          <div>
            <h1 className={styles.mobileTitle}>Hey, {getUserFirstName()}</h1>
            <p className={styles.mobileDate}>{currentDate}</p>
          </div>
        </header>
        <div className={styles.mobileNeeds}><NeedsYouList /></div>
        <TheDesk />
      </div>
    )
  }

  return (
    <div className={styles.dashboard}>
      {showTeamWelcome && user && (() => {
        const role = (user as any).role as string
        const roleLabel = role === 'assistant' ? 'Assistant' : role === 'read only' ? 'Read-Only Member' : 'Agent'
        const roleDesc = role === 'assistant'
          ? 'You can create and update records. Billing and admin settings are restricted.'
          : role === 'read only'
          ? 'You have view-only access to all agency data.'
          : 'You can manage clients, prospects, deals, and tasks.'
        return (
          <div className={styles.notice}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
              <span style={{ fontSize: '1.25rem' }}>👋</span>
              <div>
                <span style={{ color: '#fff', fontWeight: 600, fontSize: '0.9rem' }}>
                  Welcome to the team! You're signed in as an <strong>{roleLabel}</strong>.
                </span>
                <span style={{ color: '#8B9299', fontSize: '0.85rem', marginLeft: '0.5rem' }}>{roleDesc}</span>
              </div>
            </div>
            <button
              onClick={() => {
                localStorage.setItem(`team_welcome_seen_${user.id}`, 'true')
                setPref('team_welcome_seen', true)
                setShowTeamWelcome(false)
              }}
              className={styles.noticeClose}
              aria-label="Dismiss"
            >
              ×
            </button>
          </div>
        )
      })()}

      {hasDemoDataState && !demoBannerDismissed && (
        <div className={`${styles.notice} ${styles.noticeSample}`}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
            <span style={{ fontSize: '1.125rem' }}>🎯</span>
            <span style={{ color: '#fde68a', fontWeight: 500, fontSize: '0.9rem' }}>
              You're exploring with sample data.
            </span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexShrink: 0 }}>
            <button onClick={handleClearDemo} disabled={clearingDemo} className={styles.noticeAction}>
              {clearingDemo ? 'Clearing...' : 'Clear sample data'}
            </button>
            <button onClick={() => setDemoBannerDismissed(true)} className={styles.noticeClose} aria-label="Dismiss">×</button>
          </div>
        </div>
      )}

      <div className={styles.deskGrid}>
        <div className={styles.deskMain}>
          <NeedsYouList />

          <OnboardingChecklist
            hasClients={clients.length > 0}
            hasDeals={deals.length > 0}
            hasProfile={Boolean(user?.name)}
            hasProspects={prospects.length > 0}
            hasContracts={contracts.length > 0}
            hasTeamMembers={teamMembers.length > 1}
            hasCommissionSettings={Boolean(settingsQuery.data)}
            userName={getUserFirstName()}
          />

          {/* Captured conversations Reppr would not guess at. Renders nothing
              when there are none, which is most days. */}
          <CaptureInbox />
          <ContactSuggestions />

          {blockerNote}

          {/* Where the book stands. Mine / team / all filters the position;
              the decisions above are everyone's. */}
          <div className={styles.positionHead}>
            <h2 className={styles.positionTitle}>Where the book stands</h2>
            <WorkViewToggleInline />
          </div>
          <TheDesk />
        </div>

        <aside className={styles.deskRail}>
          <MoneyThisWeek enabled={hasAgency} isAssistant={isAssistant} />
          <SayItToClaude />
        </aside>
      </div>

      {canInstall && (
        <div className={styles.installBanner}>
          <span>Install Reppr for a better experience</span>
          <button onClick={promptInstall} className={styles.installBtn}>Install App</button>
        </div>
      )}

      <OnboardingTour />
      {showOnboardingWizard && <OnboardingWizard onComplete={dismissOnboardingWizard} />}
    </div>
  )
}
